import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { timelyIntegrationCloudV1UserSchema } from '@timelyapp/timely-api-spec';
import { z } from 'zod';
import { AccountRoleSchema } from './models.accounts';

extendZodWithOpenApi(z);

export const TimelyWebhookHeadersSchema = z.object({
  authorization: z.string().optional().openapi({
    example: 'Bearer 123e4567-e89b-12d3-a456-426614174000',
    description:
      'Use the `INTERNAL_TIMELY_WEBHOOK_API_KEY` key. Localhost dev does NOT require this, but production does.',
  }),
});

/**
 * The response TIC sends to Timely after syncing user and accounts
 * See /sync/ API endpoints.
 */
export type TicResponseToTimelySyncUserAndAccounts = z.infer<typeof SyncTimelyToTicRpcSchema>;
export const SyncTimelyToTicRpcSchema = z.object({
  tic_user: z.object({
    id: z.string().uuid().openapi({
      example: '123e4567-e89b-12d3-a456-426614174000',
      description: 'The TIC `user.id`',
    }),
    _status_change: z
      .enum(['none', 'created', 'updated'])
      .describe('Indicates if the TIC user has been changed as a result of this sync')
      .optional(),
  }),
  timely_user: timelyIntegrationCloudV1UserSchema,
  tic_accounts: z.array(
    z.object({
      id: z.string().uuid().openapi({
        example: '123e4567-e89b-12d3-a456-426614174000',
        description: 'The TIC `account.id`',
      }),
      timely_account_id: z
        .number()
        .nullable()
        .openapi({ example: 4301, description: 'The Timely `account.id`' }),
      type: z
        .enum(['personal', 'account'])
        .openapi({ example: 'personal', description: 'The TIC `account.type`' }),
      role: AccountRoleSchema,
      account_owner: timelyIntegrationCloudV1UserSchema.optional(),
      synced_with_timely_at: z.string().datetime({ offset: true }).nullable(),
    })
  ),
});

export type MintReadOnlyTokenRequest = z.infer<typeof MintReadOnlyTokenRequestSchema>;
export const MintReadOnlyTokenRequestSchema = z.object({
  expiresInMinutes: z.coerce
    .number()
    .int()
    .min(1)
    .max(1440)
    .default(60)
    .openapi({ example: 60, description: 'Token lifetime in minutes (max 24h)' }),
});

export type MintReadOnlyTokenResponse = z.infer<typeof MintReadOnlyTokenResponseSchema>;
export const MintReadOnlyTokenResponseSchema = z.object({
  jwt: z.string().openapi({
    description: 'A TIC JWT carrying `scope: read_only` — send as the `X-TIC-JWT` header',
    example:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
  }),
  expiresInMinutes: z.number().openapi({ example: 60 }),
  expiresAt: z.string().datetime({ offset: true }).openapi({ example: '2026-08-21T13:37:00.000Z' }),
});

export type SyncTimelyToTicResponse = z.infer<typeof SyncTimelyToTicResponseSchema>;
export const SyncTimelyToTicResponseSchema = SyncTimelyToTicRpcSchema.pick({
  tic_user: true,
  tic_accounts: true,
}).extend({
  jwt: z.string().openapi({
    example: `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c`,
  }),
  jwtValidInMinutes: z.number().openapi({ example: 60 }),
  /**
   * Describe the cookie to Timely, so it can be set in response to /tic/sessions
   */
  cookieInstructions: z
    .object({
      name: z.string().openapi({ example: 'tic-session' }),
      value: z.string().openapi({
        description: 'Cookie value (JWT token)',
        example: `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6Ik1lbmRvIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c`,
      }),
      domain: z.string().openapi({ example: '.app.timelyapp.com' }),
    })
    .optional()
    .openapi({ description: 'Instructions to Timely for setting httpOnly cookie' }),
});
